/**
 * Utilitaires de table des matières (TOC) — partagés entre le Centre d'aide
 * pleine page (KnowledgeArticle) et le Help Drawer.
 *
 * Source UNIQUE de la génération d'ancres : `slugifyHeading` doit rester
 * strictement identique à l'ID posé par `HeadingRenderer` (markdownRenderers)
 * sinon les liens `#id` ne correspondent plus aux titres rendus.
 */

export interface TocItem {
    id: string
    text: string
    level: number
}

/**
 * Génère l'ID d'ancre d'un titre.
 *
 * ⚠️ `HeadingRenderer` (`renderers/markdownRenderers.tsx`) APPELLE cette
 * fonction : il n'existe donc qu'une implémentation, et l'ancre écrite dans un
 * article et l'`id` posé sur le titre ne peuvent pas diverger.
 *
 * 🔴 **Les accents sont TRANSLITÉRÉS, plus jetés.** `\w` vaut `[A-Za-z0-9_]`
 * en JavaScript : `[^\w-]` SUPPRIMAIT donc chaque lettre accentuée, et
 * « Dépendances entre permissions » rendait `dpendances-entre-permissions` —
 * un mot amputé, que personne ne devine en écrivant un lien. Quatre ancres du
 * corpus étaient mortes : le lecteur cliquait et rien ne bougeait.
 *
 * La transliération (`normalize('NFKD')` puis retrait des diacritiques) est
 * l'idiome DÉJÀ employé par `normaliserPourRecherche` (`knowledge/uiConfig.ts`)
 * — la même question, résolue de la même façon.
 */
export function slugifyHeading(text: string): string {
    return text
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '')
}

/** Extrait les titres H2 d'un contenu markdown pour la table des matières. */
export function extractToc(content: string): TocItem[] {
    const headingRegex = /^(##)\s+(.+)$/gm
    const items: TocItem[] = []
    let match: RegExpExecArray | null

    while ((match = headingRegex.exec(content)) !== null) {
        const level = match[1].length
        const text = match[2].trim()
        items.push({ id: slugifyHeading(text), text, level })
    }

    return items
}
